// src/features/rooms/hooks/useRooms.js
import { useState, useCallback } from 'react';
import { roomsAPI } from '../services/rooms';
import { useApi } from '../../../hooks/api/useApi';
import { useAuth } from '../../../context/AuthContext';

export function useRooms() {
  const [rooms, setRooms] = useState([]);
  const [currentRoom, setCurrentRoom] = useState(null);
  const { loading, error, execute, clearError } = useApi();
  const { user } = useAuth();
  // Depend on the id, not the user object: /me refreshes replace the object and
  // would otherwise refetch the list every time
  const departmentId = user?.department?.department_id;

  const fetchDepartmentRooms = useCallback(async (includeInactive = false) => {
    if (!departmentId) return { success: false, error: 'No department ID' };
    
    const result = await execute(() => 
      roomsAPI.listRoomsByDepartment(departmentId, { include_inactive: includeInactive })
    );
    if (result.success) {
      setRooms(result.data.rooms || []);
    }
    return result;
  }, [execute, departmentId]);

  const fetchRoomById = useCallback(async (roomId) => {
    const result = await execute(() => roomsAPI.getRoomById(roomId));
    if (result.success) {
      setCurrentRoom(result.data);
    }
    return result;
  }, [execute]);

  const createRoom = useCallback(async (data) => {
    const result = await execute(() => roomsAPI.createRoom(data));
    if (result.success) {
      await fetchDepartmentRooms();
    }
    return result;
  }, [execute, fetchDepartmentRooms]);

  const updateRoom = useCallback(async (roomId, data) => {
    const result = await execute(() => roomsAPI.updateRoom(roomId, data));
    if (result.success) {
      setRooms(prev => prev.map(r =>
        r.room_id === roomId ? { ...r, ...result.data } : r
      ));
      if (currentRoom?.room_id === roomId) {
        setCurrentRoom({ ...currentRoom, ...result.data });
      }
    }
    return result;
  }, [execute, currentRoom]);

  const deactivateRoom = useCallback(async (roomId, reason) => {
    const result = await execute(() => roomsAPI.deactivateRoom(roomId, { reason }));
    if (result.success) {
      await fetchDepartmentRooms(true);
    }
    return result;
  }, [execute, fetchDepartmentRooms]);

  const reactivateRoom = useCallback(async (roomId) => {
    const result = await execute(() => roomsAPI.reactivateRoom(roomId));
    if (result.success) {
      await fetchDepartmentRooms(true);
    }
    return result;
  }, [execute, fetchDepartmentRooms]);

  const deleteRoom = useCallback(async (roomId) => {
    const result = await execute(() => roomsAPI.deleteRoom(roomId));
    if (result.success) {
      setRooms(prev => prev.filter(r => r.room_id !== roomId));
    }
    return result;
  }, [execute]);

  return {
    rooms,
    currentRoom,
    loading,
    error,
    clearError,
    fetchDepartmentRooms,
    fetchRoomById,
    createRoom,
    updateRoom,
    deactivateRoom,
    reactivateRoom,
    deleteRoom,
  };
}